/**
 * The repo-level summary line: what the choice is *between*.
 *
 * A GGUF repo's bare URL renders the picker, not a model — `autoGgufVariant`
 * refuses to pick when there is more than one variant, deliberately. So the
 * share card for that URL cannot depict "the model"; what it can depict
 * honestly is the choice itself: how many quantizations, spanning what.
 *
 * **One function, two surfaces.** The card prints this under the repo id and
 * the picker prints it as its subhead, because a visitor arriving from a shared
 * repo-level link lands on the *picker* — so "someone arriving from a shared
 * link must read the figures they just saw" (CLAUDE.md) is satisfied on that
 * surface or nowhere. A second implementation is one edit from the two
 * disagreeing about a model, which is the same argument `anatomyStatsLine`
 * exists for, applied one level out.
 *
 * **Display only, and it must stay that way.** This orders variants to find the
 * span's endpoints; it does not and may not know which one the card defaults
 * to. That rule lives in `og/src/default-variant.ts` and runs in exactly one
 * runtime — the moment this function learns the default, there is a second
 * spelling of the selection to drift, which is precisely the class that produced
 * both bugs recorded in `hf/component-label.ts`.
 *
 * **No params, no bits per weight.** Bytes are safe by nature — a byte is a byte
 * however it is packed — but a parameter count taken from the Hub's `gguf.total`
 * would be a second source of truth for the quantity `modelParamCount` owns,
 * which is the invariant's fifth-instance rule verbatim. The picker's per-row
 * `≈bpw` gets away with it because every row is hedged; a card has no room to
 * hedge.
 */

import { formatBytes } from "../ui/format.js";

/**
 * One variant reduced to what this line needs.
 *
 * `bytes` is the variant's **total** across its parts — a split variant costed
 * by its first part alone would understate a three-part BF16 by two thirds and
 * look entirely plausible beside its neighbours. `costOf` does that summing;
 * this takes the answer.
 */
export interface SummaryVariant {
  readonly label: string;
  /** `undefined` when the Hub did not report a size for some part of it. */
  readonly bytes: number | undefined;
}

/**
 * `24 quantizations · IQ2_XXS → BF16 · 8.39 GiB – 50.9 GiB`, or a shorter
 * honest version.
 *
 * **Pass the variant list, never the repo's `.gguf` files.**
 * `unsloth/Qwen3.8-27B-GGUF` ships `mmproj-F16.gguf` at 0.9 GB beside its 22
 * real quantizations, so a line built from the file listing states a vision
 * projector as the smallest quantization and counts 24 subjects where there are
 * 22. `findGgufVariants` already excludes the auxiliary class
 * (`AUXILIARY_ANYWHERE`); the caller's job is to use it.
 *
 * Degrades rather than lies: with any size missing the span is dropped and the
 * count stands alone, because a span computed over the subset we happened to
 * know would silently narrow.
 */
export function ggufRepoSummary(
  variants: readonly SummaryVariant[],
): string | undefined {
  if (variants.length === 0) return undefined;
  const count = `${variants.length} quantization${variants.length === 1 ? "" : "s"}`;

  const costed: { readonly label: string; readonly bytes: number }[] = [];
  for (const variant of variants) {
    if (variant.bytes === undefined) return count;
    costed.push({ label: variant.label, bytes: variant.bytes });
  }

  /*
   * Min and max directly rather than by sorting, so this shares no ordering with
   * the default-variant rule. They cannot disagree about the endpoints whatever
   * that rule's tie-break turns out to be, and there is nothing here for a later
   * edit to quietly reuse as "the order the default is picked from".
   *
   * Ties broken by label so the line is a pure function of the file list: two
   * variants at identical byte counts are not rare (`Q4_0` and `Q4_K_S` differ
   * by 6 MiB on the Qwen3.8 fixture and could coincide elsewhere), and a card is
   * regenerated on demand — `ops/card.sh --delete` must reproduce it exactly.
   */
  let low = costed[0] as { label: string; bytes: number };
  let high = low;
  for (const entry of costed) {
    if (
      entry.bytes < low.bytes ||
      (entry.bytes === low.bytes && entry.label < low.label)
    ) {
      low = entry;
    }
    if (
      entry.bytes > high.bytes ||
      (entry.bytes === high.bytes && entry.label > high.label)
    ) {
      high = entry;
    }
  }

  // One variant, or several that happen to be one size: a span from a value to
  // itself reads as a bug in the card rather than a fact about the repo.
  if (low.label === high.label) return `${count} · ${formatBytes(high.bytes)}`;

  return `${count} · ${low.label} → ${high.label} · ${formatBytes(low.bytes)} – ${formatBytes(high.bytes)}`;
}
